import {
    AeaExerciseModel,
    AeaGroupedExercisesContent,
    AvailableUserOwnSubjectsInfo,
    UserOwnCard,
    UserSubject,
} from "p6m-p6u";
import moment from "moment";
import type { MakePxpRequest } from "../context/AppGeneralContext";
import { sanitizeText } from "./TestCreation";

export interface ParsedUserUnit {
    id: string;
    name: string;
    order: number;
}

export interface UserUnitWithCards {
    unit: ParsedUserUnit;
    cards: Array<UserOwnCard>;
}

interface UserUnitIdResponse {
    id?: string;
}

interface UserUnitDataResponse {
    name?: string;
    order?: number;
}

/** Loads every unit of an own subject together with its (sanitized) cards. */
export async function fetchUserUnitsWithCards(
    makePxpRequest: MakePxpRequest,
    subjectUuid: string
): Promise<Array<UserUnitWithCards>> {
    const unitsResp = await makePxpRequest("GET", `userUnitsContents/${subjectUuid}`);
    const unitObjects: Array<[UserUnitIdResponse, UserUnitDataResponse]> = unitsResp?.data?.replyContent?.objects || [];
    const units: ParsedUserUnit[] = unitObjects
        .map(([idToOwner, data]) => ({ id: idToOwner?.id, name: data?.name || "", order: data?.order ?? 0 }))
        .filter((u): u is ParsedUserUnit => typeof u.id === "string" && u.id.length > 0);

    return Promise.all(
        units.map(async (unit) => {
            const resp = await makePxpRequest("GET", `userCardsContents/${subjectUuid}/${unit.id}`);
            const cards = processUserUnitCardsResponse(resp?.data?.replyContent?.objects || [], subjectUuid, unit.id);
            return { unit, cards };
        })
    );
}

/**
 * Ownership is the reliable signal: isOwnContentTask is only set by processExercisesListResponse,
 * so trainings read via mergeAndFilterCardsTestListResponse never carry it.
 */
export function isOwnContentTraining(training: AeaExerciseModel, userId: string): boolean {
    return !!training.isOwnContentTask || training.subjectContentId?.ownerId === userId;
}

/** For own content subjectContentId holds a user subject rather than a book, so it yields null. */
export function resolveArticleUuid(training: AeaExerciseModel, userId: string): string | null {
    return isOwnContentTraining(training, userId) ? null : training.subjectContentId?.id ?? null;
}

/**
 * The metadata row's `json` for an own-content training: the ownSubjectInfo shape tests send, which
 * recent-articles reads in place of articleUuid. Empty strings, not omissions, for absent fields.
 */
export function buildOwnSubjectMetadataJson(subject: UserSubject): string {
    return JSON.stringify({
        ownSubjectInfo: {
            id: subject.id ?? "",
            ownerId: subject.ownerId ?? "",
            name: subject.name ?? "",
            primaryLang: subject.primaryLang ?? "",
            secondaryLang: subject.secondaryLang ?? "",
        },
    });
}

/**
 * Undefined for book trainings, and for own-content trainings whose subject info hasn't been loaded
 * yet — callers fall back to the row's cached json in that case rather than writing a subject-less
 * row that drops the training out of recent-articles.
 */
export function resolveOwnSubjectMetadataJson(
    training: AeaExerciseModel,
    userId: string,
    ownSubjectsInfo: AvailableUserOwnSubjectsInfo
): string | undefined {
    const subjectId = training.subjectContentId?.id;
    if (!subjectId || !isOwnContentTraining(training, userId)) return undefined;
    const subject = ownSubjectsInfo[subjectId];
    return subject ? buildOwnSubjectMetadataJson({ ...subject, id: subjectId }) : undefined;
}

export interface ResolveMetadataJsonParams {
    training: AeaExerciseModel;
    userId: string;
    ownSubjectsInfo: AvailableUserOwnSubjectsInfo;
    // The row's stored json, if any.
    storedJson: string | undefined;
    // The TestCreationContext loader: resolves undefined when a subject can't be fetched.
    loadOwnSubjectsInfo: (
        subjectIds: Array<string>,
        options?: { silent?: boolean }
    ) => Promise<AvailableUserOwnSubjectsInfo | undefined>;
}

/**
 * The json a re-POSTed row must carry for an own-content training — the writes rebuild the whole row,
 * so an undefined json would drop the training out of recent-articles. Fresh subject info wins and is
 * loaded on demand, since the trainings list doesn't populate it; the stored json is the fallback when
 * that fails. Anything not own content keeps its stored json unchanged.
 */
export async function resolveMetadataJson(params: ResolveMetadataJsonParams): Promise<string | undefined> {
    const { training, userId, ownSubjectsInfo, storedJson, loadOwnSubjectsInfo } = params;
    const subjectId = training.subjectContentId?.id;
    if (!subjectId || !isOwnContentTraining(training, userId)) return storedJson;

    const fromLoadedInfos = resolveOwnSubjectMetadataJson(training, userId, ownSubjectsInfo);
    if (fromLoadedInfos) return fromLoadedInfos;

    try {
        // Silent: this is a best-effort write, so no spinner mid-share and no modal after a successful one.
        const loadedInfos = await loadOwnSubjectsInfo([subjectId], { silent: true });
        return resolveOwnSubjectMetadataJson(training, userId, loadedInfos ?? {}) ?? storedJson;
    } catch (e) {
        console.error("Failed to load the own subject for the vocab training metadata", e);
        return storedJson;
    }
}

export function processUserUnitCardsResponse(responseData: any, subjectId: string, unitId: string): Array<UserOwnCard> {
    let returnData: Array<UserOwnCard> = [];

    if (responseData.length) {
        responseData.forEach(([cId, cardContent]: any) => {
            let newCard: UserOwnCard = { ...cId, ...cardContent };
            if (newCard.answer && newCard.answer.indexOf("[") >= 0) {
                newCard.answer = newCard.answer.split("[")[0];
            }
            newCard.answer = sanitizeText(newCard.answer || "");

            if (newCard.question && newCard.question.indexOf("[")) {
                newCard.question = newCard.question.split("[")[0];
            }
            newCard.question = sanitizeText(newCard.question || "");

            if (newCard.answer.indexOf(":image~}") >= 0 || newCard.question.indexOf(":image~}") >= 0) {
                return;
            }

            if (newCard.answer.trim().length === 0 || newCard.question.trim().length === 0) {
                return;
            }
            newCard.isUserCard = true;
            newCard.subjectId = subjectId;
            newCard.unitId = unitId;

            returnData.push(newCard);
        });
    }

    return returnData;
}

/**
 * cardsTestList returns both self-created tests (sharedTestId.id === "") and
 * "exercises" that the vocab trainer creates when the same user accepts a
 * share link.
 */
export function isSelfCreatedVocabTraining(exercise: AeaExerciseModel): boolean {
    return !exercise.sharedTestId?.id;
}

/**
 * cardsTestWithShare answers with the link it just minted as its replyContent. The trainings list can
 * lag behind on a fresh training, so this is the only reliable source right after a create.
 * When no link was minted, pxp substitutes a generic "<Type> with ID … was updated" text instead,
 * so only an http(s) URL counts as a link.
 */
export function extractShareLinkFromCreateResponse(responseData: unknown): string {
    const replyContent = (responseData as { replyContent?: unknown } | undefined)?.replyContent;
    return typeof replyContent === "string" && /^https?:\/\//.test(replyContent) ? replyContent : "";
}

export function mergeAndFilterCardsTestListResponse(responseData: any): Array<AeaExerciseModel> {
    if (!Array.isArray(responseData) || responseData.length === 0) {
        return [];
    }

    const merged: Array<AeaExerciseModel> = responseData.map((i: Array<any>) => ({ ...i[0], ...i[1] }));
    return merged.filter(isSelfCreatedVocabTraining);
}

export function processExercisesListResponse(responseData: any, userId: string): AeaGroupedExercisesContent {
    const mergedExercises = mergeAndFilterCardsTestListResponse(responseData);
    let exercisesInGroup: { [key: string]: Array<AeaExerciseModel> } = {};
    let groupedExercises: AeaGroupedExercisesContent = {};

    mergedExercises.forEach((e) => {
        if (!e.groupId || !e.subjectContentId || !e.subjectContentId.id) {
            return;
        }
        if (exercisesInGroup.hasOwnProperty(e.groupId)) {
            exercisesInGroup[e.groupId].push(e);
        } else {
            exercisesInGroup[e.groupId] = [e];
        }
    });

    let lastCreatedExerciseId = localStorage.getItem("lsRt");

    for (const [key, exercisesArray] of Object.entries(exercisesInGroup)) {
        let bookedGroups: { [key: string]: Array<AeaExerciseModel> } = {};

        exercisesArray.forEach((exercise) => {
            if (!exercise.groupId || !exercise.subjectContentId || !exercise.subjectContentId.id) {
                return;
            }

            exercise.wasCreatedLast = !!lastCreatedExerciseId && lastCreatedExerciseId === exercise.id;
            exercise.isOwnContentTask = exercise.subjectContentId.ownerId === userId;

            if (bookedGroups.hasOwnProperty(exercise.subjectContentId.id)) {
                bookedGroups[exercise.subjectContentId.id].push(exercise);
            } else {
                bookedGroups[exercise.subjectContentId.id] = [exercise];
            }
        });

        Object.keys(bookedGroups).forEach((bookId) => {
            bookedGroups[bookId].sort((e1, e2) => moment(e2.createdDate).unix() - moment(e1.createdDate).unix());
        });

        groupedExercises = { ...groupedExercises, [key]: bookedGroups };
    }

    localStorage.removeItem("lsRt");

    return groupedExercises;
}
